/**
 * Sqft → boxes PDP calculator.
 * Entered sqft (min–max) × (1 + selected overage) → ceil ÷ sq_ft_per_box → form quantity (boxes).
 * See docs/dev/docs-box-quantity-calculator.md
 */
if (!customElements.get('box-quantity-calculator')) {
  customElements.define(
    'box-quantity-calculator',
    class BoxQuantityCalculator extends HTMLElement {
      connectedCallback() {
        this.sqftInput = this.querySelector('[data-box-sqft]')
        this.overageSelect = this.querySelector('[data-overage-select]')
        this.boxCountEl = this.querySelector('[data-box-count]')
        this.totalEl = this.querySelector('[data-box-total]')
        this.qtyInput = this.querySelector('.quantity__input')
        this.missingMessage = this.querySelector('[data-box-missing-message]')
        this.atcButton = document.querySelector('.product-form__submit')
        this.variantIdInput =
          this.closest('product-info')?.querySelector('.product-variant-id') ||
          document.querySelector(
            `#product-form-${this.dataset.sectionId} [name="id"]`
          ) ||
          document.querySelector('[name="id"]')

        this.minSqft = parseFloat(this.dataset.minSqft) || 25
        this.maxSqft = parseFloat(this.dataset.maxSqft) || 400
        this.coverageByVariant = this.readCoverage()

        this.onInput = this.onInput.bind(this)
        this.onVariantChange = this.onVariantChange.bind(this)

        this.sqftInput?.addEventListener('input', this.onInput)
        this.sqftInput?.addEventListener('change', this.onInput)
        this.addEventListener('overage:change', this.onInput)

        if (
          typeof subscribe === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined'
        ) {
          this.variantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.variantChange,
            this.onVariantChange
          )
        }

        this.recalculate()
      }

      disconnectedCallback() {
        this.sqftInput?.removeEventListener('input', this.onInput)
        this.sqftInput?.removeEventListener('change', this.onInput)
        this.removeEventListener('overage:change', this.onInput)
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber()
      }

      readCoverage() {
        const script = this.querySelector('[data-box-coverage]')
        if (!script) return {}
        try {
          return JSON.parse(script.textContent.trim())
        } catch (e) {
          console.error('box-quantity-calculator: invalid coverage JSON', e)
          return {}
        }
      }

      currentVariantId() {
        const id = this.variantIdInput?.value
        return id ? String(id) : null
      }

      sqFtPerBox() {
        const id = this.currentVariantId()
        if (!id) return null
        const value = this.coverageByVariant[id]
        if (value == null || value === '') return null
        const num = parseFloat(value)
        return Number.isFinite(num) && num > 0 ? num : null
      }

      overagePercent() {
        const value = parseFloat(this.overageSelect?.value)
        return Number.isFinite(value) && value > 0 ? value : 0
      }

      onVariantChange(event) {
        const sectionId = this.dataset.sectionId
        if (event?.data?.sectionId && event.data.sectionId !== sectionId) return
        // Variant id input may update after publish; wait a tick.
        requestAnimationFrame(() => this.recalculate())
      }

      onInput() {
        this.recalculate()
      }

      recalculate() {
        const sqFtPerBox = this.sqFtPerBox()
        const entered = parseFloat(this.sqftInput?.value)

        if (!sqFtPerBox) {
          this.setDisplay('—', '—')
          this.setQuantity(0)
          this.toggleMissing(true)
          this.setAtcEnabled(false)
          return
        }

        this.toggleMissing(false)

        if (
          !Number.isFinite(entered) ||
          entered < this.minSqft ||
          entered > this.maxSqft
        ) {
          this.setDisplay('—', '—')
          this.setQuantity(0)
          this.setAtcEnabled(false)
          return
        }

        const effective = entered * (1 + this.overagePercent() / 100)
        const boxes = Math.max(1, Math.ceil(effective / sqFtPerBox))
        const total = boxes * sqFtPerBox

        this.setDisplay(String(boxes), total.toFixed(this.dataset.coveragePrecision === '2' ? 2 : 1))
        this.setQuantity(boxes)
        this.setAtcEnabled(true)
      }

      setDisplay(boxesText, totalText) {
        if (this.boxCountEl) this.boxCountEl.textContent = boxesText
        if (this.totalEl) this.totalEl.textContent = totalText
      }

      setQuantity(boxes) {
        if (!this.qtyInput) return
        this.qtyInput.value = String(boxes)
        this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }))
      }

      toggleMissing(missing) {
        this.missingMessage?.classList.toggle('hidden', !missing)
      }

      setAtcEnabled(enabled) {
        if (!this.atcButton) return
        if (enabled) {
          this.atcButton.removeAttribute('disabled')
        } else {
          this.atcButton.setAttribute('disabled', 'true')
        }
      }
    }
  )
}
